<template>
    <div aria-live="assertive" class="online-tests-new-page">
        <div class="col-xs-12">
            <div class="d-flex-row flex-justify-between">
                <p id="title" role="heading" aria-level="1" tabindex="0">
                    <i class="fa fa-globe"></i> Provas online
                </p>
            </div>
        </div>

        <div v-if="!loading">
            <div v-if="result && result.length > 0">
                <div class="col-xs-12 p-x-sm d-flex-row flex-wrap">
                    <div class="col-xs-12 p-md m-b-md">
                        <div class="card p-20">
                            <div class="search-input-wrapper m-b-xl">
                                <i class="fa fa-search"></i>
                                <input
                                    type="text"
                                    v-model="searchQuery"
                                    placeholder="Buscar por disciplina ou avaliação..."
                                    class="search-input"
                                />
                            </div>

                            <div class="d-flex-row filter-group">
                                <div class="filter-group__section">
                                    <FilterNew
                                        v-model="selectedData"
                                        label="Módulo"
                                        icon="fa fa-filter"
                                        :options="dataOptions"
                                    />
                                </div>
                                <div class="filter-group__divider"></div>
                                <div class="filter-group__section">
                                    <FilterNew
                                        v-model="selectedType"
                                        label="Tipo"
                                        :options="typeOptions"
                                    />
                                </div>
                                <span class="info filter-group__count">
                                    <strong>{{ totalCount }}</strong> {{ totalCount === 1 ? 'avaliação' : 'avaliações' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        v-for="test in sortedTests"
                        :key="test.item_generated"
                        class="col-xs-12 col-md-6 col-lg-3 p-md m-b-md"
                    >
                        <TestCard
                            :test="test"
                            :module-name="test.data"
                            :days-until-due="test.daysUntilDue"
                        />
                    </div>
                </div>

                <div v-if="filteredTests.length === 0" class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                    <img src="../../assets/img/search-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                    <div class="d-flex-column">
                        <div class="no-results-title">Nenhum resultado encontrado</div>
                        <div>Não há registros que atendam aos filtros aplicados</div>
                    </div>
                </div>
            </div>

            <div v-else class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                <img src="../../assets/img/no-results-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                <div class="d-flex-column">
                    <div class="no-results-title">Nenhum resultado encontrado</div>
                    <div>Não há provas disponíveis para serem realizadas.</div>
                </div>
            </div>
        </div>

        <div v-else role="presentation" class="col-xs-12 d-flex-column align-center flex-justify-center text-center h-100vh overflow-hidden">
            <i class="fa fa-5x fa-circle-o-notch fa-spin"></i>
        </div>
    </div>
</template>

<script>
import { getOnlineTests } from "../../scripts/apiService/tests";
import { extractModule } from "../../scripts/utils";
import FilterNew from "../base/FilterNew.vue";
import TestCard from "../base/TestCard.vue";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export default {
    name: 'OnlineTestsListNew',
    components: {
        FilterNew,
        TestCard
    },
    data() {
        return {
            result: null,
            loading: false,
            searchQuery: '',
            selectedData: 'all',
            selectedType: 'all'
        };
    },
    computed: {
        totalCount() {
            return this.result?.length || 0;
        },

        dataOptions() {
            const modules = new Set();
            if (this.result) {
                this.result.forEach(test => {
                    const module = test.module_name || extractModule(test.academic);
                    if (module) modules.add(module);
                });
            }
            const sortedModules = Array.from(modules).sort();
            return [
                { value: 'all', label: 'Todos' },
                ...sortedModules.map(m => ({ value: m, label: m }))
            ];
        },

        typeOptions() {
            const types = new Set();
            if (this.result) {
                this.result.forEach(test => {
                    if (test.schedule?.type_assessment?.name) {
                        types.add(test.schedule.type_assessment.name);
                    }
                });
            }
            const sortedTypes = Array.from(types).sort();
            return [
                { value: 'all', label: 'Todos' },
                ...sortedTypes.map(t => ({ value: t, label: t }))
            ];
        },

        testsWithData() {
            if (!this.result) return [];

            return this.result.map(test => ({
                ...test,
                daysUntilDue: test.days_until_due ?? this.calcDaysUntilDue(test),
                data: test.module_name || extractModule(test.academic)
            }));
        },

        filteredTests() {
            let tests = this.testsWithData;

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                tests = tests.filter(test =>
                    test.academic.toLowerCase().includes(query)
                );
            }

            if (this.selectedData !== 'all') {
                tests = tests.filter(test => test.data === this.selectedData);
            }

            if (this.selectedType !== 'all') {
                tests = tests.filter(test => test.schedule.type_assessment.name === this.selectedType);
            }

            return tests;
        },

        sortedTests() {
            return [...this.filteredTests].sort((a, b) => {
                const moduleA = a.data || '';
                const moduleB = b.data || '';
                const moduleCompare = moduleA.localeCompare(moduleB);
                if (moduleCompare !== 0) return moduleCompare;

                const dateA = a.end_date || a.schedule?.date_schedule || '';
                const dateB = b.end_date || b.schedule?.date_schedule || '';
                return new Date(dateA) - new Date(dateB);
            });
        }
    },
    methods: {
        calcDaysUntilDue(test) {
            const dateStr = test.end_date || test.schedule?.date_schedule;
            if (!dateStr) return null;
            const end = new Date(dateStr);
            const now = new Date();
            const diff = Math.ceil((end - now) / MS_PER_DAY);
            return Math.max(0, diff);
        }
    },
    mounted() {
        this.loading = true;
        getOnlineTests(this.$route.params.key).then((response) => {
            this.result = response.data;
            this.loading = false;
        });
        document.getElementById('title').focus();
    }
}
</script>
